import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription, combineLatest, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BlacksmithingJournalService } from './blacksmithing-journal.service';
import { JournalEntry, ForgePhoto } from './blacksmithing-journal.model';
import { BLACKSMITHING_MODULES } from './blacksmithing.data';

@Component({
    selector: 'app-blacksmithing-journal',
    templateUrl: './blacksmithing-journal.component.html',
    styleUrls: ['./blacksmithing-journal.component.scss'],
    standalone: false
})
export class BlacksmithingJournalComponent implements OnInit, OnDestroy {
  modules = BLACKSMITHING_MODULES;
  entries: JournalEntry[] = [];
  photosByEntry: { [entryId: string]: ForgePhoto[] } = {};

  userId: string | null = null;
  loading = true;

  // Streak
  streak = 0;
  daysSinceLastForge: number | null = null;

  // Form state
  showForm = false;
  editingEntry: JournalEntry | null = null;
  formData: Partial<JournalEntry> = {};
  formError = '';
  saving = false;

  // File upload
  selectedFiles: File[] = [];
  filePreviews: string[] = [];
  uploadProgress = 0;

  // Photo actions
  lightboxPhoto: ForgePhoto | null = null;
  lightboxCaption = '';
  editingCaptionId: string | null = null;
  captionDraft = '';

  private authSub?: Subscription;
  private entriesSub?: Subscription;
  private photoSubs: Subscription[] = [];

  constructor(
    private auth: AngularFireAuth,
    private journalService: BlacksmithingJournalService
  ) {}

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadEntries();
      } else {
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.entriesSub?.unsubscribe();
    this.photoSubs.forEach(s => s.unsubscribe());
  }

  private loadEntries() {
    if (!this.userId) return;
    this.entriesSub?.unsubscribe();
    this.entriesSub = this.journalService.getEntries(this.userId).subscribe(entries => {
      this.entries = entries;
      this.loading = false;
      this.calculateStreak();
      this.loadPhotosForEntries();
    });
  }

  private loadPhotosForEntries() {
    if (!this.userId) return;
    this.photoSubs.forEach(s => s.unsubscribe());
    this.photoSubs = [];
    this.photosByEntry = {};

    this.entries.forEach(entry => {
      const sub = this.journalService.getPhotosForEntry(this.userId!, entry.id).subscribe(photos => {
        this.photosByEntry = { ...this.photosByEntry, [entry.id]: photos };
      });
      this.photoSubs.push(sub);
    });
  }

  private calculateStreak() {
    const dates = [...new Set(this.entries.map(e => e.date))].sort().reverse();
    if (!dates.length) {
      this.streak = 0;
      this.daysSinceLastForge = null;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDate = new Date(dates[0] + 'T00:00:00');
    this.daysSinceLastForge = Math.floor((today.getTime() - lastDate.getTime()) / 86400000);

    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = new Date(today.getTime() - 86400000).toISOString().split('T')[0];

    if (dates[0] !== todayStr && dates[0] !== yesterdayStr) {
      this.streak = 0;
      return;
    }

    let streak = 1;
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i - 1] + 'T00:00:00');
      const curr = new Date(dates[i] + 'T00:00:00');
      const diff = Math.round((prev.getTime() - curr.getTime()) / 86400000);
      if (diff === 1) streak++;
      else break;
    }
    this.streak = streak;
  }

  get streakLabel(): string {
    if (this.daysSinceLastForge === null) return 'No sessions yet — fire up the forge!';
    if (this.daysSinceLastForge === 0) return `🔥 ${this.streak}-day streak — forged today!`;
    if (this.daysSinceLastForge === 1 && this.streak > 0) return `🔥 ${this.streak}-day streak — forge today to keep it alive`;
    if (this.daysSinceLastForge === 1) return 'Last forged yesterday';
    return `Last forged ${this.daysSinceLastForge} days ago`;
  }

  getModuleTitle(moduleId: string | undefined): string {
    if (!moduleId) return '';
    return this.modules.find(m => m.id === moduleId)?.title ?? '';
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  openNewForm() {
    this.editingEntry = null;
    const today = new Date().toISOString().split('T')[0];
    this.formData = { date: today };
    this.formError = '';
    this.selectedFiles = [];
    this.filePreviews = [];
    this.showForm = true;
  }

  openEditForm(entry: JournalEntry) {
    this.editingEntry = entry;
    this.formData = { ...entry };
    this.formError = '';
    this.selectedFiles = [];
    this.filePreviews = [];
    this.showForm = true;
  }

  closeForm() {
    this.showForm = false;
    this.editingEntry = null;
    this.formData = {};
    this.selectedFiles = [];
    this.filePreviews = [];
    this.uploadProgress = 0;
    this.saving = false;
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    const files = Array.from(input.files).slice(0, 10);
    this.selectedFiles = [...this.selectedFiles, ...files].slice(0, 10);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviews.push(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
    input.value = '';
  }

  removeSelectedFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.filePreviews.splice(index, 1);
  }

  async saveEntry() {
    if (!this.userId || !this.formData.date) {
      this.formError = 'Date is required.';
      return;
    }

    this.saving = true;
    this.formError = '';

    try {
      const entryData: Omit<JournalEntry, 'id' | 'createdAt'> = {
        date: this.formData.date,
        ...(this.formData.moduleId && { moduleId: this.formData.moduleId }),
        ...(this.formData.project && { project: this.formData.project }),
        ...(this.formData.heats != null && this.formData.heats > 0 && { heats: this.formData.heats }),
        ...(this.formData.sessionLength && { sessionLength: this.formData.sessionLength }),
        ...(this.formData.whatWentWell && { whatWentWell: this.formData.whatWentWell }),
        ...(this.formData.challenges && { challenges: this.formData.challenges }),
        ...(this.formData.notes && { notes: this.formData.notes }),
      };

      let entryId: string;
      if (this.editingEntry) {
        entryId = this.editingEntry.id;
        await this.journalService.updateEntry(this.userId, { ...this.editingEntry, ...entryData });
      } else {
        entryId = await this.journalService.addEntry(this.userId, entryData);
      }

      // Upload photos
      if (this.selectedFiles.length > 0) {
        let uploaded = 0;
        for (const file of this.selectedFiles) {
          await this.journalService.uploadPhoto(
            this.userId,
            file,
            entryId,
            this.formData.date!
          );
          uploaded++;
          this.uploadProgress = Math.round((uploaded / this.selectedFiles.length) * 100);
        }
      }

      this.closeForm();
    } catch (err) {
      this.formError = 'Something went wrong saving your entry. Please try again.';
      this.saving = false;
    }
  }

  async deleteEntry(entry: JournalEntry) {
    if (!this.userId) return;
    const photos = this.photosByEntry[entry.id] || [];
    const confirmed = confirm(
      `Delete this session from ${this.formatDate(entry.date)}?` +
      (photos.length ? ` This will also delete ${photos.length} photo${photos.length > 1 ? 's' : ''}.` : '')
    );
    if (!confirmed) return;
    await this.journalService.deleteEntry(this.userId, entry, photos);
  }

  // ── Photo actions ─────────────────────────────────────────────────────────

  openLightbox(photo: ForgePhoto) {
    this.lightboxPhoto = photo;
  }

  closeLightbox() {
    this.lightboxPhoto = null;
    this.editingCaptionId = null;
  }

  startEditCaption(photo: ForgePhoto) {
    this.editingCaptionId = photo.id;
    this.captionDraft = photo.caption || '';
  }

  async saveCaption(photo: ForgePhoto) {
    if (!this.userId) return;
    await this.journalService.updatePhotoCaption(this.userId, photo.id, this.captionDraft);
    this.editingCaptionId = null;
    if (this.lightboxPhoto?.id === photo.id) {
      this.lightboxPhoto = { ...photo, caption: this.captionDraft };
    }
  }

  async deletePhoto(photo: ForgePhoto) {
    if (!this.userId) return;
    if (!confirm('Delete this photo?')) return;
    if (this.lightboxPhoto?.id === photo.id) this.closeLightbox();
    await this.journalService.deletePhoto(this.userId, photo);
  }

  async deletePhotoFromLightbox(photo: ForgePhoto) {
    await this.deletePhoto(photo);
  }

  getPhotos(entryId: string): ForgePhoto[] {
    return this.photosByEntry[entryId] || [];
  }

  async addPhotosToEntry(event: Event, entry: JournalEntry) {
    if (!this.userId) return;
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const files = Array.from(input.files).slice(0, 10);
    input.value = '';
    for (const file of files) {
      await this.journalService.uploadPhoto(this.userId, file, entry.id, entry.date);
    }
  }
}
